import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { EventType } from "@starsach/shared-types";
import { useCustomers } from "../api/hooks";
import { Card, DataTable, DayWindowToggle, EmptyState, ErrorState, LoadingState, PageHeader, RelativeTime, StatusPill, vtag } from "../components/ui";
import { useUiStore } from "../store/ui";

/** Customers and Trusted Advisor lists are the same component over a different event source (design.md "MVP views"). */
export function CustomerListView({ source, title, countLabel, actions }: { source: EventType; title: string; countLabel: string; actions?: ReactNode }) {
  const window = useUiStore((s) => s.window);
  const setWindow = useUiStore((s) => s.setWindow);
  const q = useCustomers(window, source);

  return (
    <>
      <PageHeader
        title={title}
        subtitle={q.data ? `${q.data.customers.length} accounts · template ${vtag(q.data.currentTemplateVersion)} is current` : undefined}
        actions={<>{actions}<DayWindowToggle value={window} onChange={setWindow} /></>}
      />
      {q.isPending && <LoadingState label="Loading customers" />}
      {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
      {q.data && (
        <Card>
          <DataTable
            caption={`${title} for the last ${window} days`}
            rows={q.data.customers}
            rowKey={(c) => c.environmentId}

            sortBy={(c) => c.friendlyName}
            empty={<EmptyState title="No customers yet" hint="Onboard one with the onboarding runbook." />}
            columns={[
              { key: "name", header: "Customer", render: (c) => <Link className="font-medium text-accent-strong underline" to={source === "trusted_advisor" ? `/trusted-advisor/${c.environmentId}` : `/customers/${c.environmentId}`}>{c.friendlyName}</Link> },
              { key: "account", header: "AWS Account", render: (c) => <span className="font-mono text-xs">{c.awsAccountNumber}</span> },
              {
                key: "count", header: `${countLabel} (${window}d)`, numeric: true,
                // An account without the plan is not "0 findings" - it is unread (the owner's ask of 2026-09-21).
                render: (c) => (source === "trusted_advisor" && c.trustedAdvisorAvailable === false ? <span className="whitespace-nowrap text-xs text-neutral-500">needs a Business or Enterprise support plan</span> : c.eventCount),
              },
              { key: "last", header: "Last received", render: (c) => <RelativeTime iso={c.lastEventAt} /> },
              { key: "critical", header: "Open critical", numeric: true, render: (c) => (c.openCriticalAlarms ? <span className="font-semibold text-red-700 dark:text-red-300">{c.openCriticalAlarms}</span> : 0) },
              { key: "template", header: "Template", render: (c) => (c.templateVersion ? <span className="inline-flex items-center gap-1"><span className="font-mono text-xs">{vtag(c.templateVersion)}</span>{c.templateVersion !== q.data.currentTemplateVersion && <StatusPill status="behind" tone="warning" />}</span> : <span className="text-neutral-500">-</span>) },
              { key: "connectivity", header: "Connectivity", render: (c) => connectivity(c, q.data.currentTemplateVersion) },
            ]}
          />
        </Card>
      )}
    </>
  );
}

function connectivity(c: { status: string; onboardingStatus: string; lastHeartbeatAt?: string; forwarderDlqDepth?: number }, _current: string) {
  if (c.status !== "active") return <StatusPill status={c.status} tone="neutral" />;
  if (c.onboardingStatus !== "active") return <StatusPill status="pending" tone="warning" />;
  const stale = !c.lastHeartbeatAt || Date.now() - Date.parse(c.lastHeartbeatAt) > 3 * 3_600_000;
  return (
    <span className="flex flex-wrap items-center gap-1">
      <StatusPill status={stale ? "no heartbeat" : "reporting"} tone={stale ? "warning" : "ok"} />
      {(c.forwarderDlqDepth ?? 0) > 0 && <StatusPill status={`${c.forwarderDlqDepth} undelivered`} tone="critical" />}
    </span>
  );
}

export function CustomersPage() {
  return <CustomerListView source="cloudwatch_alarm" title="Customers" countLabel="Alarms" />;
}
